/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, NgZone, OnChanges} from '@angular/core';
import * as _ from 'lodash';
import template from './report-schedule-edit-page.component.html';
import {AppEventBusService} from '../common/events/app-event-bus.service';

import {AdnSelectSearchElement} from '../common/directives/adn-select/adn-select.component';
import {AdminResourceEditPageBase} from '../common/controller/admin-resource-edit-page-base';
import {ADN_REPORT_TYPES} from '../report/report-types';
import {EmailHelper} from '../../components/util/email';
import {
  afterSaveAfterInitMods,
  beforeSubmitMods,
  filterReportTemplates
} from './report-schedule-commons';
import {ReportScheduleModalService} from './report-schedule-modal.service';
import {ReportTemplateResource} from '../../components/api/resources/resource/report-template.resource';
import {UserResource} from '../../components/api/resources/resource/user.resource';
import {AdnTimeShifterService} from '../../components/util/adn-time-shifter.service';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {LocalUserProfile} from '../../components/session/local-user-profile';
import {SearchResource} from '../../components/api/resources/resource/search';
import {UserProfileSaverService} from '../../components/session/user-profile-saver.service';

@Component({
  standalone: false,
  selector: 'adn-report-schedule-edit-page',
  template
})
export class ReportScheduleEditPageComponent extends AdminResourceEditPageBase implements OnChanges {
  readonly weekDays = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];

  availableCurrencies: string[] = [];
  reportTemplates: any[] = [];
  allAdvertisers = false;
  recipientEmailsSaved: {success?: boolean} | null = null;

  lineItemResults: any[] = [];
  adUnitResults: any[] = [];
  orderResults: any[] = [];
  siteResults: any[] = [];
  siteGroupResults: any[] = [];
  salespersonResults: any[] = [];
  earningsAccountResults: any[] = [];
  advertiserResults: any[] = [];

  private readonly scheduleLocale: string;
  private readonly preferredCurrency: string;
  private readonly timeZone: string;

  constructor(
    eventBus: AppEventBusService,
    @Inject(UserProfileSaverService) UserProfileSaver: any,
    @Inject(LocalNetworkProfile) LocalNetworkProfile: any,
    @Inject(LocalUserPermissions) private readonly LocalUserPermissions: any,
    @Inject(LocalUserProfile) localUserProfile: any,
    @Inject(AdnTimeShifterService) private readonly adnTimeShifter: any,
    @Inject(UserResource) private readonly User: any,
    @Inject(SearchResource) private readonly searchResource: any,
    @Inject(ReportTemplateResource) private readonly ReportTemplate: any,
    private readonly reportScheduleModalService: ReportScheduleModalService,
    ngZone: NgZone
  ) {
    super(eventBus, UserProfileSaver, LocalNetworkProfile, ngZone);
    this.scheduleLocale = localUserProfile.get('locale') || 'en_GB';
    this.preferredCurrency = localUserProfile.getPreferredCurrency();
    this.timeZone = LocalNetworkProfile.get('timeZone') || 'UTC';
  }

  get lineItemReportTemplates(): any[] {
    return _.filter(this.reportTemplates, {reportType: 'LINE_ITEM'});
  }

  get reportType(): string | undefined {
    return this.editingModel?.reportTemplate?.reportType;
  }

  onFieldChange(): void {
    this.markExternalFormDirty();
  }

  editRecipients(): void {
    this.reportScheduleModalService.openRecipientsModal({
      recipientUsers: this.editingModel.recipientUsers,
      isNew: this.isNew,
      scheduleModel: this.isNew ? null : this.model
    }, (result) => {
      if (!result) {
        return;
      }
      if (result.recipientUsers) {
        this.editingModel.recipientUsers = result.recipientUsers;
      }
      if (result.validationWarnings) {
        this.editingModel.validationWarnings = result.validationWarnings;
      }
      if (result.success !== undefined) {
        this.recipientEmailsSaved = {success: result.success};
      } else if (result.recipientUsers) {
        this.markExternalFormDirty();
      }
    });
  }

  searchLineItems(searchElement: AdnSelectSearchElement): void {
    this.searchResource.query({q: searchElement.search, types: 'LineItem'}).then((data: any) => {
      this.lineItemResults = data.searchResults;
    });
  }

  searchAdUnits(searchElement: AdnSelectSearchElement): void {
    this.searchResource.query({q: searchElement.search, types: 'AdUnit'}).then((data: any) => {
      this.adUnitResults = data.searchResults;
    });
  }

  searchOrders(searchElement: AdnSelectSearchElement): void {
    this.searchResource.query({q: searchElement.search, types: 'Order'}).then((data: any) => {
      this.orderResults = data.searchResults;
    });
  }

  searchSites(searchElement: AdnSelectSearchElement): void {
    this.searchResource.query({q: searchElement.search, types: 'Site'}).then((data: any) => {
      this.siteResults = data.searchResults;
    });
  }

  searchSiteGroups(searchElement: AdnSelectSearchElement): void {
    this.searchResource.query({q: searchElement.search, types: 'SiteGroup'}).then((data: any) => {
      this.siteGroupResults = data.searchResults;
    });
  }

  searchSalesperson(searchElement: AdnSelectSearchElement): void {
    this.searchResource.query({q: searchElement.search, types: 'User'}).then((data: any) => {
      this.salespersonResults = data.searchResults;
    });
  }

  searchEarningsAccounts(searchElement: AdnSelectSearchElement): void {
    this.searchResource.query({q: searchElement.search, types: 'EarningsAccount'}).then((data: any) => {
      this.earningsAccountResults = data.searchResults;
    });
  }

  searchAdvertisers(searchElement: AdnSelectSearchElement): void {
    this.searchResource.query({q: searchElement.search, types: 'Advertiser'}).then((data: any) => {
      this.advertiserResults = data.searchResults;
    });
  }

  protected afterInitEditingModel(): void {
    this.availableCurrencies = this.LocalNetworkProfile.get('availableCurrencies');
    this.combineEmails();

    if (this.isNew) {
      this.editingModel.period = ADN_REPORT_TYPES.LINE_ITEM.defaultPeriod.id;
      this.editingModel.recurrence = 'WEEKLY';
      this.editingModel.timeZone = this.LocalNetworkProfile.get('timeZone') || 'UTC';
      this.editingModel.locale = this.scheduleLocale;
      this.allAdvertisers = false;
    }

    this.allAdvertisers = this.allAdvertisers || _.get(this.editingModel.parameters || {}, 'ALL_ACTIVE', false);
    this.editingModel.currency = this.editingModel.currency || this.preferredCurrency || this.LocalNetworkProfile.get('defaultCurrency');

    if (this.model.recipientUsers?.length > 0) {
      this.User.query({ids: _.map(this.model.recipientUsers, 'id')}, (page: any) => {
        const allUsers = page.results;
        _.forEach(this.editingModel.recipientUsers, (ru: any) => {
          const match = _.find(allUsers, ['id', ru.id]);
          if (match) {
            ru.displayName = match.displayName;
          }
        });
      });
    }

    const perms = this.LocalUserPermissions.getAllNetworkPermissions();
    const anyPerms = this.LocalUserPermissions.getAllVisPermissions();
    const networkDefaults = this.LocalNetworkProfile.getDefaults();
    const canSeeAdvertiserReports = networkDefaults.runReportsOptions.indexOf('ADVERTISER_REPORTS') > -1;

    this.ReportTemplate.query((page: any) => {
      this.ngZone.run(() => {
        this.reportTemplates = filterReportTemplates(page.results, perms, anyPerms, canSeeAdvertiserReports);
        const selRepTemplate = _.find(this.reportTemplates, ['id', _.get(this.model, ['reportTemplate', 'id'])]);
        if (selRepTemplate) {
          this.editingModel.reportTemplate = selRepTemplate;
        }
      });
    });
  }

  protected afterInitAfterSave(): void {
    this.combineEmails();
  }

  protected onBeforeSubmit(): void {
    this.editingModel.parameters = this.editingModel.parameters || {};

    _.forEach(ADN_REPORT_TYPES, (rt) => {
      delete this.editingModel.parameters[rt.idKey];
    });
    delete this.editingModel.parameters.relatedLineItem;
    delete this.editingModel.parameters[ADN_REPORT_TYPES.ADVERTISER.allActiveFlag];

    const theReportType = ADN_REPORT_TYPES[this.editingModel.reportTemplate.reportType];
    if (theReportType === ADN_REPORT_TYPES.ADVERTISER && this.allAdvertisers) {
      this.editingModel.parameters[ADN_REPORT_TYPES.ADVERTISER.allActiveFlag] = true;
    } else {
      if (theReportType === ADN_REPORT_TYPES.LINE_ITEM) {
        this.editingModel.parameters[theReportType.idKey] = this.editingModel.lineItem.id;
        this.editingModel.parameters.relatedLineItem = [{id: this.editingModel.lineItem.id}];
      } else if (theReportType === ADN_REPORT_TYPES.NETWORK) {
        this.editingModel.parameters[theReportType.idKey] = this.LocalNetworkProfile.getNetworkId();
      } else {
        this.editingModel.parameters[theReportType.idKey] = this.editingModel[theReportType.objKey].id;
      }
    }

    beforeSubmitMods(this.editingModel, this.timeZone);

    this.editingModel.recipientEmailAddresses = _.filter(_.map((this.editingModel.recipientEmailAddressesCombined || '').split(','), (email) => {
      return _.trim(email);
    }), (email) => {
      return email.length > 0 && EmailHelper.isEmail(email);
    });
  }

  private combineEmails(): void {
    this.editingModel.recipientEmailAddressesCombined = (this.editingModel.recipientEmailAddresses || []).join(', ');
    afterSaveAfterInitMods(this.editingModel, this.adnTimeShifter, this.timeZone, this.scheduleLocale);
  }
}
